import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  organizationWebhooksService,
  type OrganizationWebhookDelivery,
  type OrganizationWebhookEventCatalogEntry,
} from "../services/organizationWebhooksService";
import {
  buildDeliveriesQuery,
  DEFAULT_DELIVERIES_FILTERS,
  hasActiveDeliveriesFilters,
  type DeliveriesFilterState,
} from "../utils/organizationWebhooks.utils";

/**
 * Deliberately small: the report lives inside the scrolling Settings page, so a long page would push the
 * pagination control below the fold and make the reader scroll back up to move between pages.
 */
export const DELIVERIES_PER_PAGE = 10;

export interface OrganizationWebhookDeliveriesData {
  rows: OrganizationWebhookDelivery[];
  total: number;
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  /**
   * A page the reader is waiting for: the first load, a filter change, or a page change. The table renders
   * its skeleton on this, because the rows still on screen answer the _previous_ query and showing them as
   * though they matched the new filters would be a lie.
   */
  isLoading: boolean;
  /** A manual refresh of the same query. Spins the button only — the rows on screen are still valid. */
  isRefreshing: boolean;
  hasError: boolean;
  refresh: () => void;

  filters: DeliveriesFilterState;
  setFilters: (next: DeliveriesFilterState) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** `type → event[]` from the backend catalog; empty until it loads. */
  eventTypes: OrganizationWebhookEventCatalogEntry[];

  /** Manual re-queue. Resolves the updated row (now `pending`), or null when the backend refused. */
  retry: (id: string) => Promise<OrganizationWebhookDelivery | null>;
  isRetrying: boolean;
}

/**
 * The delivery report: one server-paginated page under the current filters, plus the retry action.
 *
 * Filter state is local (not URL-backed) on purpose: this lives inside `/app/settings`, whose only query
 * param is the `?section=` scroll anchor, and mirroring six filters into it would fight the page's own URL
 * handling. `enabled` gates the read so nothing is fetched while the organization has no endpoint yet.
 */
export function useOrganizationWebhookDeliveries(enabled: boolean): OrganizationWebhookDeliveriesData {
  const { t } = useTranslations("settings");

  const [rows, setRows] = useState<OrganizationWebhookDelivery[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<DeliveriesFilterState>(DEFAULT_DELIVERIES_FILTERS);
  const [eventTypes, setEventTypes] = useState<OrganizationWebhookEventCatalogEntry[]>([]);

  const [hasError, setHasError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  /**
   * Identity of the query on screen. Every input the request is built from is in here, so comparing it
   * against the last settled one answers "is the reader waiting?" for the first load, a filter change and a
   * page change alike — which a `hasLoadedOnce` flag could not do.
   */
  const requestKey = useMemo(() => JSON.stringify([filters, page, reloadKey]), [filters, page, reloadKey]);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const isFetching = enabled && settledKey !== requestKey;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    organizationWebhooksService
      .getDeliveries(buildDeliveriesQuery(filters, page, DELIVERIES_PER_PAGE))
      .then((result) => {
        if (cancelled) return;
        setRows(result.data);
        setTotal(result.total);
        setHasError(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Access is decided by the config read upstream (the section hides on 403); here a denial is just
        // an error state, so the table never spins forever.
        setHasError(true);
        if (!HttpApiService.isAccessDeniedError(error)) colocaToast.error(t("webhooks_deliveries_error"));
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(requestKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, filters, page, requestKey, t]);

  // The catalog is static; one read per mount is enough, and a failure only costs the dropdown options.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    organizationWebhooksService
      .getEventTypes()
      .then((catalog) => {
        if (!cancelled) setEventTypes(catalog);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  const setFilters = useCallback((next: DeliveriesFilterState) => {
    setFiltersState((current) => {
      const same =
        current.status === next.status &&
        current.type === next.type &&
        current.event === next.event &&
        current.status_code === next.status_code &&
        current.from === next.from &&
        current.to === next.to;
      return same ? current : next;
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => setFilters(DEFAULT_DELIVERIES_FILTERS), [setFilters]);

  const retry = useCallback(
    async (id: string): Promise<OrganizationWebhookDelivery | null> => {
      setIsRetrying(true);
      try {
        const updated = await organizationWebhooksService.retryDelivery(id);
        // Re-queuing a DELIVERED event returns a NEW delivery, so there is no row to patch and the
        // total has changed — the report has to be re-read or the new attempt would be invisible.
        // Everything else is the same row coming back with a new status.
        const isResend = updated.id !== id;
        if (isResend) {
          refresh();
        } else {
          setRows((current) => current.map((row) => (row.id === updated.id ? updated : row)));
        }
        colocaToast.success(t(isResend ? "webhooks_toast_resend_queued" : "webhooks_toast_retry_queued"));
        return updated;
      } catch (error: unknown) {
        colocaToast.error(
          HttpApiService.isAccessDeniedError(error)
            ? t("webhooks_error_no_write_permission")
            : HttpApiService.getErrorMessage(error),
        );
        return null;
      } finally {
        setIsRetrying(false);
      }
    },
    [refresh, t],
  );

  return useMemo(
    () => ({
      rows,
      total,
      page,
      perPage: DELIVERIES_PER_PAGE,
      setPage,
      // A manual refresh re-runs the SAME query, so its rows stay valid and only the button spins.
      isLoading: isFetching && !isRefreshing,
      isRefreshing,
      hasError,
      refresh,
      filters,
      setFilters,
      clearFilters,
      hasActiveFilters: hasActiveDeliveriesFilters(filters),
      eventTypes,
      retry,
      isRetrying,
    }),
    [
      rows,
      total,
      page,
      isFetching,
      isRefreshing,
      hasError,
      refresh,
      filters,
      setFilters,
      clearFilters,
      eventTypes,
      retry,
      isRetrying,
    ],
  );
}
